/**
 * Platen glue for product sync (Phase 86): from a product's synced variants to
 * the shared platen grouping and coverage test.
 *
 * This file holds NO grouping, ordering or coverage rule of its own. Which
 * platen variant a size uses (override, then rule), the group order and what
 * "covered" means all come from `@threadheads/shared`
 * (`groupSizesByPlatenVariant`, `isPlatenSideCovered`), so the colour grid, the
 * set-mode groups, the missing-files dialog and the Product updates worklist
 * cannot disagree.
 *
 * Which SKU a variant uploads to, its product and size codes and whether it is
 * dual-sided come from the shared `artworkTargetForVariant` (Phase 88, D-04):
 * the declared identity when the variant has one and its declared SKU is its
 * live SKU, otherwise the derivation (`derived.productCode`,
 * `derived.sizeCode`), exactly as before. Nothing here parses a SKU.
 */
import {
  artworkTargetForVariant,
  compareSizeCodes,
  groupSizesByPlatenVariant,
  isPlatenSideCovered,
  platenRuleTableFor,
  requiredPlatenSides,
  ruledTableKeyFor,
  type ArtworkTarget,
  type PlatenPrintSide,
  type PlatenSideFileState,
  type PlatenSizeAssignment,
  type PlatenSizeOverrides,
  type PlatenSizeRulesMap,
} from '@threadheads/shared';
import { chunkGids } from './shared-stock';
import type { PreviewProduct, PreviewVariant } from './types';

/** The org's platen config as `productSync.getPlatenSizeContext` returns it. */
export interface PlatenSizeContextData {
  rules: PlatenSizeRulesMap;
  /** Keyed by Shopify product gid, then uppercase size code. */
  overrides: Record<string, PlatenSizeOverrides>;
  /** An unscoped read left some products' overrides out (never, scoped by product). */
  overridesTruncated?: boolean;
}

/** One upload target in a platen group: its SKU, size, colour and dual flag. */
export interface PlatenGroupItem {
  sku: string;
  sizeCode: string | null;
  /** The lowercased colour name ('' for none), as `skusByColour` keys it. */
  colour: string;
  /** The SKU prints both sides (a declared `2TEE` SKU); never true for a derived one. */
  dualSided: boolean;
}

/** One platen variant of one product: the sizes and synced SKUs it covers. */
export interface PlatenGroup {
  /** `platenVariantKey(assignment)`, for example `14x16:Small`. Sent on commit. */
  key: string;
  assignment: PlatenSizeAssignment;
  /** Distinct size codes, smallest first (per product type, when a product mixes types). */
  sizes: string[];
  /** Every synced SKU in this group, smallest size first. */
  skus: string[];
  /** Synced SKUs per colour, keyed by the lowercased colour name ('' for none). */
  skusByColour: Record<string, string[]>;
  /** Every target in this group (`platenGroupsForProduct` sets it; absent on hand-built groups). */
  items?: PlatenGroupItem[];
}

export interface ProductPlatenGroups {
  /**
   * The rule table key for this product's type: a derived product's own code,
   * or a declared product's code as the rules hold it (a dual `2TEE` with only
   * a `TEE` table reads `TEE`).
   */
  productTypeCode: string;
  groups: PlatenGroup[];
  /** Size codes with no rule and no override: they get no slot until added. */
  uncovered: string[];
}

/**
 * One SKU's platen file state: the marker for each side (`null` means no
 * marker), and whether an artwork link exists for each side. The link flags
 * stand in for "a served file exists" (the page cannot see storage); a SKU
 * with a link keeps printing its current file until an upload replaces it.
 * Missing flags read as "no link".
 */
export interface PlatenSkuFileState {
  front: PlatenSideFileState | null;
  back: PlatenSideFileState | null;
  hasFrontUrl?: boolean;
  hasBackUrl?: boolean;
  /** BLU-1238 (R3-2): the SKU's product keeps printing its current files. Present only when true. */
  platenSkipped?: true;
  /** Round 4: the side's exact Small file was made from Large. Present only when true (and asked for). */
  frontMadeFromLarge?: true;
  backMadeFromLarge?: true;
}

/** Per-SKU platen file state; a SKU not in the map has no marker and no link. */
export type PlatenFileStateBySku = Record<string, PlatenSkuFileState>;

/** The fields of a `productSync.getPlatenFileState` row the page reads (flags, never a URL). */
export interface PlatenFileStateRow {
  sku_code: string;
  has_front_url: boolean;
  has_back_url: boolean;
  front_platen?: string | null;
  front_platen_variant?: string | null;
  front_exact?: boolean;
  back_platen?: string | null;
  back_platen_variant?: string | null;
  back_exact?: boolean;
  /**
   * BLU-1238 (R3-2): the SKU's product keeps printing its current files (an
   * ACTIVE skip platen mark). Folded server side: never true for a supply row.
   */
  platenSkipped?: boolean;
  /** Round 4, only when the read asked `includeMadeFromLarge`. */
  front_made_from_large?: boolean;
  back_made_from_large?: boolean;
}

function sideFromRow(
  platen: string | null | undefined,
  platenVariant: string | null | undefined,
  exact: boolean | undefined,
): PlatenSideFileState | null {
  if ((platen ?? null) === null && (platenVariant ?? null) === null) return null;
  return { platen: platen ?? null, platenVariant: platenVariant ?? null, exact: exact === true };
}

/** The ONE mapping from a file-state row to `PlatenSkuFileState`, for every reader. */
export function platenFileStateFromRow(row: PlatenFileStateRow): PlatenSkuFileState {
  return {
    front: sideFromRow(row.front_platen, row.front_platen_variant, row.front_exact),
    back: sideFromRow(row.back_platen, row.back_platen_variant, row.back_exact),
    hasFrontUrl: row.has_front_url === true,
    hasBackUrl: row.has_back_url === true,
    ...(row.platenSkipped === true ? { platenSkipped: true as const } : {}),
    ...(row.front_made_from_large === true ? { frontMadeFromLarge: true as const } : {}),
    ...(row.back_made_from_large === true ? { backMadeFromLarge: true as const } : {}),
  };
}

/** Round 4: the SKU's `side` holds the Small file made from Large (from the file state). */
export function isSideMadeFromLarge(state: PlatenSkuFileState | null | undefined, side: 'front' | 'back'): boolean {
  return side === 'front' ? state?.frontMadeFromLarge === true : state?.backMadeFromLarge === true;
}

/** Rows to a by-SKU map (a later row for the same SKU wins). */
export function platenFileStateBySku(rows: Iterable<PlatenFileStateRow>): PlatenFileStateBySku {
  const out: PlatenFileStateBySku = {};
  for (const row of rows) out[row.sku_code] = platenFileStateFromRow(row);
  return out;
}

// ---------------------------------------------------------------------------
// Required sides: which sides a ruled size owes a platen variant file for.
// ---------------------------------------------------------------------------

/**
 * The shared `requiredPlatenSides` (the same rule the Product updates worklist
 * and print time use) for a SKU the page holds file state for. A derived
 * product sync SKU is always single-sided (the shared `syncedSku` default), so
 * `dualSided` defaults to false; a declared dual SKU (`2TEE`) passes its own
 * flag from `artworkTargetForVariant`.
 */
export function requiredSidesForSku(
  state: PlatenSkuFileState | null | undefined,
  dualSided = false,
): PlatenPrintSide[] {
  return requiredPlatenSides({
    dualSided,
    hasFrontUrl: state?.hasFrontUrl === true,
    hasBackUrl: state?.hasBackUrl === true,
    hasFrontFile: (state?.front ?? null) !== null,
    hasBackFile: (state?.back ?? null) !== null,
  });
}

/**
 * Whether any of these SKUs owes `side` a platen variant file. The colour
 * grid's missing notice and its per-cell chip both ask this, so a front-only
 * design never shows its Back cells as missing (and a back-only design never
 * shows its Front cells as missing). `dualSkus` names the SKUs that print
 * both sides (a declared dual SKU owes a back even before it has one).
 */
export function isPlatenSideRequired(
  skus: readonly string[],
  side: PlatenPrintSide,
  fileState: PlatenFileStateBySku,
  dualSkus?: ReadonlySet<string>,
): boolean {
  return skus.some((sku) => requiredSidesForSku(fileState[sku], dualSkus?.has(sku) === true).includes(side));
}

/** The dual-sided SKUs of these groups, for `isPlatenSideRequired`. */
export function platenDualSkus(groups: readonly PlatenGroup[]): Set<string> {
  const out = new Set<string>();
  for (const group of groups) for (const item of group.items ?? []) if (item.dualSided) out.add(item.sku);
  return out;
}

/** The colour key the grid rows use. */
export function platenColourKey(colourName: string | null | undefined): string {
  return colourName ? colourName.toLowerCase() : '';
}

/** True when the org rules at least one size of at least one product type. */
export function hasAnyPlatenRule(rules: PlatenSizeRulesMap): boolean {
  return Object.values(rules).some((table) => Object.keys(table).length > 0);
}

/**
 * Where one variant of this product uploads to: the shared
 * `artworkTargetForVariant`. A product in store SKU mode (it carries a
 * `storeSku` summary) passes `storeSkuMode`, so it uploads only to its set-up
 * store SKUs, plus (`includeSettable`, 88a.1 D4) a not-set-up variant the
 * server says an upload can set up: one that carries `settable`, which the
 * server attaches only while it opts in. An upload there sets the product up
 * (the commit re-resolves the variant server-side and never trusts the
 * browser's codes). Any other variant without a declared row is no target,
 * even when its live SKU reads as a derived one. The server's Product updates
 * worklist passes the same options, so the two never disagree about a target.
 * The browser only ever sends SKUs and the expected platen key.
 *
 * 86-24 (R3-11): a variant product sync set up for a store SKU org (its
 * derived projection row, `projectedDerived`) uploads to that row's SKU,
 * whatever its live SKU says; the commit finds it by variant id.
 */
export function productArtworkTarget(product: PreviewProduct, variant: PreviewVariant): ArtworkTarget {
  return artworkTargetForVariant(variant, {
    // 86-24: a variant product sync set up for a store SKU org carries its
    // derived row (`projectedDerived`, store SKU mode only) and targets it.
    storeSkuMode: Boolean(product.storeSku) || Boolean(variant.projectedDerived),
    includeSettable: true,
  });
}

/**
 * The product type code a product's rules are keyed by: its first upload
 * target's code (every derived variant of one product shares it; a declared
 * or settable one carries its own), or null when there is no target. A store SKU product
 * whose every set-up SKU changed falls back to a changed variant's stored
 * code, so its changed cells still render under its rules.
 */
export function platenProductTypeCode(product: PreviewProduct): string | null {
  let changedCode: string | null = null;
  for (const variant of product.variants) {
    const target = productArtworkTarget(product, variant);
    if (target.sku !== null && target.productTypeCode) return target.productTypeCode;
    if (target.identity === 'changed' && changedCode === null) changedCode = variant.declared?.productTypeCode ?? null;
  }
  return changedCode;
}

/**
 * Whether these rules cover this product's type. The shared answer to "is
 * this type ruled" (the dual-sided `2` prefix and an empty table are handled
 * by `platenRuleTableFor`), so this page, the worklist and print time agree.
 */
export function isProductPlatenRuled(product: PreviewProduct, rules: PlatenSizeRulesMap): boolean {
  const productTypeCode = platenProductTypeCode(product);
  return !!productTypeCode && platenRuleTableFor(rules, productTypeCode) !== null;
}

/**
 * Whether product sync shows the Product updates tab. It exists only for an
 * org with platen size rules: without them nothing can ever need a platen
 * variant file. The worklist's own answer (`rulesConfigured` on the first
 * page of its walk) is the fresher one and wins; until a walk has one, the
 * page's org platen read decides. A tab the URL asked for (a print-file
 * issue's deep link) stays until the worklist itself says there are no rules,
 * and a failed org read shows the tab rather than hide real work.
 */
export function productUpdatesTabVisible(input: {
  requested: boolean;
  worklistRulesConfigured: boolean | null;
  orgHasRules: boolean | null;
  orgReadFailed: boolean;
}): boolean {
  if (input.requested) return input.worklistRulesConfigured !== false;
  return input.worklistRulesConfigured ?? input.orgHasRules ?? input.orgReadFailed;
}

/**
 * The platen variant groups for one product, or `null` when the product's type
 * has no Platen Size rules (the page then renders today's UI with no platen
 * chrome). Only upload targets take part (`productArtworkTarget`: a set-up
 * store SKU or a settable one, else the shared `syncedSku`); a size with no rule and no
 * override is listed in `uncovered`. A variant whose store SKU changed is in
 * no group (the colour grid renders it from the variants).
 *
 * Grouping runs once per type code, as the Product updates worklist does: a
 * derived product has one code, while a store SKU product may mix single and
 * dual SKUs (`TEE` and `2TEE`). Groups of the same platen variant merge, so
 * each platen variant is one group (each type's items keep their own order).
 */
export function platenGroupsForProduct(
  product: PreviewProduct,
  context: PlatenSizeContextData | null | undefined,
): ProductPlatenGroups | null {
  if (!context) return null;
  const productTypeCode = platenProductTypeCode(product);
  // The same test as `isProductPlatenRuled`, on the code already in hand.
  if (!productTypeCode || platenRuleTableFor(context.rules, productTypeCode) === null) return null;

  const byType = new Map<string, PlatenGroupItem[]>();
  const seen = new Set<string>();
  // A declared or settable code, or a store SKU changed since set-up: each
  // names the type the way the rules hold it.
  let storeSkuCode = false;
  for (const variant of product.variants) {
    const target = productArtworkTarget(product, variant);
    if (target.identity === 'changed') {
      storeSkuCode = true;
      continue;
    }
    const sku = target.sku;
    if (sku === null || seen.has(sku) || !target.productTypeCode) continue;
    seen.add(sku);
    if (target.identity === 'declared' || target.identity === 'settable') storeSkuCode = true;
    const list = byType.get(target.productTypeCode) ?? [];
    list.push({ sku, sizeCode: target.sizeCode, colour: platenColourKey(variant.colourName), dualSided: target.dualSided });
    byType.set(target.productTypeCode, list);
  }

  const overrides = Object.prototype.hasOwnProperty.call(context.overrides, product.id)
    ? context.overrides[product.id]
    : {};
  const merged = new Map<string, { assignment: PlatenSizeAssignment; items: PlatenGroupItem[] }>();
  const uncovered = new Set<string>();
  for (const [code, items] of byType) {
    const grouped = groupSizesByPlatenVariant(items, { rules: context.rules, overrides, productTypeCode: code });
    for (const group of grouped.groups) {
      const existing = merged.get(group.key);
      if (existing) existing.items.push(...group.items);
      else merged.set(group.key, { assignment: group.assignment, items: [...group.items] });
    }
    for (const item of grouped.uncovered) if (item.sizeCode !== null) uncovered.add(item.sizeCode);
  }

  return {
    // A derived code is passed through exactly as before; a declared or
    // settable one is named the way the rules hold it, for the missing-size prompt.
    productTypeCode: storeSkuCode ? ruledTableKeyFor(context.rules, productTypeCode) : productTypeCode,
    groups: [...merged].map(([key, group]) => {
      const sizes: string[] = [];
      const skusByColour: Record<string, string[]> = {};
      for (const item of group.items) {
        if (item.sizeCode !== null && !sizes.includes(item.sizeCode)) sizes.push(item.sizeCode);
        (skusByColour[item.colour] ??= []).push(item.sku);
      }
      return {
        key,
        assignment: group.assignment,
        sizes,
        skus: group.items.map((item) => item.sku),
        skusByColour,
        items: group.items,
      };
    }),
    uncovered: [...uncovered],
  };
}

/**
 * A platen cell (one colour, one side, one platen variant) is published only
 * when every SKU's marker for that side is exact and on the group's platen
 * variant (D-22). An older generic URL has no marker and does not count.
 */
export function isPlatenCellPublished(
  skus: readonly string[],
  side: 'front' | 'back',
  assignment: PlatenSizeAssignment,
  fileState: PlatenFileStateBySku,
): boolean {
  return skus.length > 0 && skus.every((sku) => isPlatenSideCovered(assignment, fileState[sku]?.[side] ?? null));
}

/**
 * BLU-1238 (R3-14): which ruled sizes of a product still have no platen file
 * on a side the design prints, and how many sizes are fully covered. The same
 * per-SKU rule as the cell chips (`requiredSidesForSku`, `isPlatenSideCovered`),
 * so a kept product's "these sizes aren't filled out" hint and the server's
 * clear check agree. Sizes come back distinct, in the ladder order.
 */
export function platenSizesWithoutFile(
  groups: readonly PlatenGroup[],
  fileState: PlatenFileStateBySku,
): { missing: string[]; covered: string[] } {
  const missing = new Set<string>();
  const seen = new Set<string>();
  for (const group of groups) {
    for (const item of group.items ?? []) {
      if (item.sizeCode === null) continue;
      seen.add(item.sizeCode);
      const state = fileState[item.sku];
      const uncovered = requiredSidesForSku(state, item.dualSided).some(
        (side) => !isPlatenSideCovered(group.assignment, state?.[side] ?? null),
      );
      if (uncovered) missing.add(item.sizeCode);
    }
  }
  const order = (sizes: Iterable<string>) => [...sizes].sort(compareSizeCodes);
  return { missing: order(missing), covered: order([...seen].filter((size) => !missing.has(size))) };
}

/**
 * BLU-1238 (R3-2): how a platen surface words a slot that still has no
 * platen variant file. The ONE place a missing size is called held:
 *  - `optional`: the product keeps printing its current files (an active skip
 *    platen mark), whatever is missing, so nothing is held;
 *  - `keeps`: an older file keeps printing until it is replaced (D-14);
 *  - `noFile`: no file at all, so the size can't print until one is uploaded.
 */
export type PlatenMissingWording = 'optional' | 'keeps' | 'noFile';

export function platenMissingWording(hasOlderFile: boolean, keepsCurrentFiles = false): PlatenMissingWording {
  if (keepsCurrentFiles) return 'optional';
  return hasOlderFile ? 'keeps' : 'noFile';
}

/** True when any of these SKUs' file state says its product keeps printing its current files. */
export function platenSkusKept(skus: Iterable<string>, fileState: PlatenFileStateBySku | undefined): boolean {
  if (!fileState) return false;
  for (const sku of skus) if (fileState[sku]?.platenSkipped === true) return true;
  return false;
}

/**
 * Stable 30-SKU chunks for `productSync.getPlatenFileState` (the GET URL cap,
 * see `LINK_STATE_GID_CHUNK`). Sorted and de-duplicated, so the same product
 * always yields the same query keys and the chunks are shared across surfaces.
 */
export function platenFileStateChunks(skus: readonly string[]): string[][] {
  return chunkGids([...new Set(skus)].sort());
}
